import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, ChevronRight, Play, BarChart3 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import AdminActionMenu from '@/components/AdminActionMenu';


const CONDITION_TYPES = [
 { value: 'add_court', label: 'Ajouter des terrains', icon: '🏀' },
 { value: 'activate_court', label: 'Valider des terrains', icon: '✅' },
 { value: 'win_duels', label: 'Gagner des duels', icon: '🏆' },
 { value: 'capture_courts', label: 'Conquérir des terrains', icon: '⚔️' },
 { value: 'defend_courts', label: 'Défendre des terrains', icon: '🛡️' },
 { value: 'community_growth', label: 'Développer la communauté', icon: '👥' }
];


export default function AdminChallenges() {
 const navigate = useNavigate();
 const [user, setUser] = useState(null);
 const [showForm, setShowForm] = useState(false);
 const [activeTab, setActiveTab] = useState('list');
 const [dialogOpen, setDialogOpen] = useState(false);
 const [dialogMessage, setDialogMessage] = useState('');
 const [dialogTitle, setDialogTitle] = useState('');
 const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
 const [challengeToDelete, setChallengeToDelete] = useState(null);
 const [mutatingChallengeId, setMutatingChallengeId] = useState(null);
 const [formData, setFormData] = useState({
   title: '',
   description: '',
   condition_type: '',
   condition_value: '',
   reward_type: 'ticket',
   reward_description: '',
   reward_value: '',
   min_level: '1',
   start_at: '',
   end_at: ''
 });
 const queryClient = useQueryClient();

 // Auto-scroll to bottom on mobile
 React.useEffect(() => {
   if (window.innerWidth < 768) {
     setTimeout(() => {
       window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
     }, 100);
   }
 }, []);

 const { data: challenges = [], refetch } = useQuery({
   queryKey: ['adminChallenges'],
   queryFn: async () => {
     const results = await base44.entities.Challenge.list('-created_date', 100);
     return results;
   },
   enabled: !!user
 });

 const { data: stats = {} } = useQuery({
   queryKey: ['adminChallengeStats'],
   queryFn: async () => {
     const [challenges, raffleEntries, draws, allProgress] = await Promise.all([
       base44.entities.Challenge.list('-created_date', 100),
       base44.entities.RaffleEntry.list('-created_date', 1000),
       base44.entities.ChallengeDraw.list('-created_date', 100),
       base44.entities.UserChallengeProgress.list('-created_date', 5000)
     ]);

     const activeChallenges = challenges.filter(c => c.is_active && c.status === 'active').length;
     const closedChallenges = challenges.filter(c => c.status === 'closed').length;
     const drawnChallenges = challenges.filter(c => c.status === 'drawn').length;

     // Count participants from both RaffleEntry AND UserChallengeProgress
     const participantIds = new Set([
       ...raffleEntries.map(e => e.user_id),
       ...allProgress.map(p => p.user_id)
     ]);
     const activeParticipants = participantIds.size;
     const totalTicketsInPlay = raffleEntries.length;
     const completedChallenges = allProgress.filter(p => p.is_completed).length;

     return {
       activeChallenges,
       closedChallenges,
       drawnChallenges,
       activeParticipants,
       totalTicketsInPlay,
       completedChallenges,
       totalChallenges: challenges.length,
       totalDrawsExecuted: draws.length
     };
   },
   enabled: !!user,
   refetchOnWindowFocus: true,
   staleTime: 0
 });


 React.useEffect(() => {
   supabase.auth.getUser().then(async ({ data: { user: authUser } }) => {
     if (!authUser) { navigate('/map'); return; }
     const { data: profile } = await supabase.from('users').select('*').eq('id', authUser.id).single();
     if (!profile || profile.role !== 'admin') {
       navigate('/map');
     } else {
       setUser({ ...authUser, ...profile });
     }
   }).catch(() => {
     navigate('/map');
   });
 }, [navigate]);


 const createChallengeMutation = useMutation({
   mutationFn: async (data) => {
     const startAt = new Date(data.start_at).toISOString();
     const endAt = new Date(data.end_at).toISOString();
    
     const { data: challenge, error } = await supabase.from('challenges').insert({
       title: data.title,
       description: data.description,
       condition_type: data.condition_type,
       condition_value: parseInt(data.condition_value),
       reward_type: data.reward_type,
       reward_description: data.reward_description,
       reward_value: parseInt(data.reward_value),
       start_at: startAt,
       end_at: endAt,
       min_level: parseInt(data.min_level) || 1,
       status: 'draft',
       is_active: false
     }).select().single();
     if (error) throw error;
     return challenge;
   },
   onSuccess: () => {
     queryClient.invalidateQueries({ queryKey: ['adminChallenges'] });
     queryClient.invalidateQueries({ queryKey: ['adminChallengeStats'] });
     refetch();
     setDialogTitle('Succès');
     setDialogMessage('Défi créé avec succès!');
     setDialogOpen(true);
     setShowForm(false);
     setFormData({
       title: '',
       description: '',
       condition_type: '',
       condition_value: '',
       reward_type: 'ticket',
       reward_description: '',
       reward_value: '',
       min_level: '1',
       start_at: '',
       end_at: ''
     });
   },
   onError: (error) => {
     setDialogTitle('Erreur');
     setDialogMessage('Erreur: ' + (error.message || 'Une erreur est survenue'));
     setDialogOpen(true);
   }
 });


 const toggleChallengeMutation = useMutation({
   mutationFn: async ({ challenge, newStatus }) => {
     // If activating and same condition_type is active, deactivate others first
     if (newStatus && challenge.condition_type) {
       const activeChallenges = challenges.filter(c =>
         c.id !== challenge.id &&
         c.condition_type === challenge.condition_type &&
         c.is_active
       );
      
       for (const c of activeChallenges) {
         await base44.entities.Challenge.update(c.id, { is_active: false });
       }
     }
    
     return base44.entities.Challenge.update(challenge.id, {
       is_active: newStatus
     });
   },
   onSuccess: () => {
     queryClient.invalidateQueries({ queryKey: ['adminChallenges'] });
     queryClient.invalidateQueries({ queryKey: ['adminChallengeStats'] });
     refetch();
   }
 });


 const statusTransitionMutation = useMutation({
   mutationFn: async ({ challenge, newStatus }) => {
     setMutatingChallengeId(challenge.id);
     const { data: { user: currentUser } } = await supabase.auth.getUser();
     const response = await base44.entities.Challenge.update(challenge.id, {
       status: newStatus
     });
    
     // Log admin action (best-effort)
     try {
       base44.entities.AdminLog?.create?.({
         admin_user_id: currentUser.id,
         action_type: newStatus === 'active' ? 'challenge_activate' : 'challenge_close',
         target_entity: 'Challenge',
         target_id: challenge.id,
         details: { new_status: newStatus },
         timestamp: new Date().toISOString()
       })?.catch?.(err => console.error('Failed to log action:', err));
     } catch (e) {
       console.error('Failed to log action:', e);
     }


     return response;
   },
   onSuccess: () => {
     queryClient.invalidateQueries({ queryKey: ['adminChallenges'] });
     queryClient.invalidateQueries({ queryKey: ['adminChallengeStats'] });
     queryClient.invalidateQueries({ queryKey: ['challengeDetail'] });
     refetch();
   },
   onSettled: () => {
     setMutatingChallengeId(null);
   }
 });


 const deleteChallengeMutation = useMutation({
   mutationFn: (challenge) => base44.entities.Challenge.delete(challenge.id),
   onSuccess: () => {
     queryClient.invalidateQueries({ queryKey: ['adminChallenges'] });
     queryClient.invalidateQueries({ queryKey: ['adminChallengeStats'] });
     refetch();
   }
 });


 const executeDrawMutation = useMutation({
   mutationFn: async (challenge) => {
     const currentDate = new Date();
     const raffle_month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    
     console.warn('[Migration] executeChallengeDraw not fully migrated yet');
     const response = { data: null };
     return response.data;
   },
   onSuccess: (data) => {
     setDialogTitle('Tirage réussi');
     setDialogMessage(`Tirage exécuté!\n${data.finalists_count} finalistes sélectionnés parmi ${data.total_participants} participants.`);
     setDialogOpen(true);
     queryClient.invalidateQueries({ queryKey: ['adminChallenges'] });
     queryClient.invalidateQueries({ queryKey: ['adminChallengeStats'] });
     refetch();
   },
   onError: (error) => {
     setDialogTitle('Erreur');
     setDialogMessage('Erreur tirage: ' + (error.response?.data?.error || error.message));
     setDialogOpen(true);
   }
 });


 const handleSubmit = () => {
   if (!formData.title || !formData.condition_type || !formData.condition_value || !formData.reward_description || !formData.reward_value || !formData.start_at || !formData.end_at) {
     setDialogTitle('Champs manquants');
     setDialogMessage('Tous les champs sont requis');
     setDialogOpen(true);
     return;
   }
   if (new Date(formData.start_at) >= new Date(formData.end_at)) {
     setDialogTitle('Dates invalides');
     setDialogMessage('La date de fin doit être après la date de début');
     setDialogOpen(true);
     return;
   }
   createChallengeMutation.mutate(formData);
 };


 if (!user) {
   return <div className="flex items-center justify-center min-h-screen text-white">Chargement...</div>;
 }


 return (
   <>
     <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
       <DialogContent className="bg-[#13151D] border-[#6E2BFF] text-white">
         <DialogTitle className="text-white">{dialogTitle}</DialogTitle>
         <DialogDescription className="text-[#B3B6C2]">{dialogMessage}</DialogDescription>
         <div className="flex justify-end gap-2 mt-4">
           <Button
             onClick={() => setDialogOpen(false)}
             className="bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white"
           >
             OK
           </Button>
         </div>
       </DialogContent>
     </Dialog>


     <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
       <DialogContent className="bg-[#13151D] border-[#6E2BFF] text-white">
         <DialogTitle className="text-white">Supprimer le défi</DialogTitle>
         <DialogDescription className="text-[#B3B6C2]">Êtes-vous sûr? Cette action est irréversible.</DialogDescription>
         <div className="flex justify-end gap-2 mt-4">
           <Button
             onClick={() => setDeleteDialogOpen(false)}
             variant="outline"
             className="border-[#6E2BFF] text-[#6E2BFF]"
           >
             Annuler
           </Button>
           <Button
             onClick={() => {
               deleteChallengeMutation.mutate(challengeToDelete);
               setDeleteDialogOpen(false);
             }}
             disabled={deleteChallengeMutation.isPending}
             className="bg-red-600 hover:bg-red-700 text-white"
           >
             Supprimer
           </Button>
         </div>
       </DialogContent>
     </Dialog>
     <div className="bg-[#0A0B10] text-white p-6">
     <div className="max-w-5xl mx-auto">
       <div className="flex items-center gap-4 mb-8">
         <Button
           variant="ghost"
           size="sm"
           className="text-white hover:text-[#6E2BFF]"
           onClick={() => navigate('/admin')}
         >
           <ArrowLeft className="w-4 h-4 mr-2" /> Retour
         </Button>
         <h1 className="text-lg font-black uppercase tracking-tight">Challenges</h1>
       </div>

       {/* Tabs */}
       <div className="flex gap-2 mb-6">
         <Button
           onClick={() => setActiveTab('list')}
           variant={activeTab === 'list' ? 'default' : 'outline'}
           className={activeTab === 'list' ? 'bg-[#6E2BFF] text-white' : 'border-[#6E2BFF] text-[#6E2BFF]'}
         >
           Challenges
         </Button>
         <Button
           onClick={() => setActiveTab('data')}
           variant={activeTab === 'data' ? 'default' : 'outline'}
           className={activeTab === 'data' ? 'bg-[#6E2BFF] text-white' : 'border-[#6E2BFF] text-[#6E2BFF]'}
         >
           <BarChart3 className="w-4 h-4 mr-2" /> Data
         </Button>
       </div>

       {activeTab === 'data' && (
         <>
           {/* Quick Stats Grid */}
           <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
             <Card className="bg-[#13151D] border-l-4 border-[#6E2BFF] p-4">
               <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-1">Actifs</p>
               <p className="text-3xl font-black text-[#6E2BFF]">{stats.activeChallenges || 0}</p>
             </Card>
             <Card className="bg-[#13151D] border-l-4 border-amber-600 p-4">
               <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-1">Fermés</p>
               <p className="text-3xl font-black text-amber-500">{stats.closedChallenges || 0}</p>
             </Card>
             <Card className="bg-[#13151D] border-l-4 border-green-600 p-4">
               <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-1">Tirés</p>
               <p className="text-3xl font-black text-green-500">{stats.drawnChallenges || 0}</p>
             </Card>
             <Card className="bg-[#13151D] border-l-4 border-orange-600 p-4">
               <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-1">Terrains en attente</p>
               <p className="text-3xl font-black text-orange-500">{stats.pendingCourts || 0}</p>
             </Card>
             <Card className="bg-[#13151D] border-l-4 border-purple-600 p-4">
               <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-1">Participants</p>
               <p className="text-3xl font-black text-purple-500">{stats.activeParticipants || 0}</p>
             </Card>
           </div>

           {/* Secondary Stats */}
           <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
             <Card className="bg-[#13151D] border-l-4 border-[#6E2BFF] p-3">
               <p className="text-xs text-[#B3B6C2] mb-1">Tickets en jeu</p>
               <p className="text-2xl font-black text-[#6E2BFF]">{stats.totalTicketsInPlay || 0}</p>
             </Card>
             <Card className="bg-[#13151D] border-l-4 border-green-600 p-3">
               <p className="text-xs text-[#B3B6C2] mb-1">Défis complétés</p>
               <p className="text-2xl font-black text-green-500">{stats.completedChallenges || 0}</p>
             </Card>
             <Card className="bg-[#13151D] border-l-4 border-[#6E2BFF] p-3">
               <p className="text-xs text-[#B3B6C2] mb-1">Total Challenges</p>
               <p className="text-2xl font-black text-[#6E2BFF]">{stats.totalChallenges || 0}</p>
             </Card>
             <Card className="bg-[#13151D] border-l-4 border-[#6E2BFF] p-3">
               <p className="text-xs text-[#B3B6C2] mb-1">Tirages exécutés</p>
               <p className="text-2xl font-black text-[#6E2BFF]">{stats.totalDrawsExecuted || 0}</p>
             </Card>
           </div>
         </>
       )}

       {activeTab === 'list' && (
         <>
       {/* Create Button */}
       <Button
         onClick={() => setShowForm(!showForm)}
         className="mb-6 bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
       >
         <Plus className="w-4 h-4 mr-2" /> Nouveau Challenge
       </Button>


       {/* Create Form */}
       {showForm && (
         <Card className="p-6 mb-8 bg-[#13151D] border-l-4 border-[#6E2BFF] space-y-4">
           <div>
             <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Titre</label>
             <Input
               placeholder="Ex: Ajouter un premier terrain"
               value={formData.title}
               onChange={(e) => setFormData({ ...formData, title: e.target.value })}
               className="bg-[#1B1E28] border-[#6E2BFF] text-white placeholder-[#B3B6C2]"
             />
           </div>
           <div>
             <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Description</label>
             <Textarea
               placeholder="Description du défi..."
               value={formData.description}
               onChange={(e) => setFormData({ ...formData, description: e.target.value })}
               className="bg-[#1B1E28] border-[#6E2BFF] text-white placeholder-[#B3B6C2]"
             />
           </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Type de défi</label>
              <Select value={formData.condition_type} onValueChange={(value) => setFormData({ ...formData, condition_type: value })}>
                <SelectTrigger className="bg-[#1B1E28] border-[#6E2BFF] text-white h-10">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1B1E28] border-[#6E2BFF]">
                  {CONDITION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white">
                      <span className="text-lg mr-2">{type.icon}</span> {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Objectif</label>
              <Input
                type="number"
                placeholder="Ex: 5"
                value={formData.condition_value}
                onChange={(e) => setFormData({ ...formData, condition_value: e.target.value })}
                className="bg-[#1B1E28] border-[#6E2BFF] text-white placeholder-[#B3B6C2]"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Niveau minimum requis</label>
            <Select value={formData.min_level} onValueChange={(value) => setFormData({ ...formData, min_level: value })}>
              <SelectTrigger className="bg-[#1B1E28] border-[#6E2BFF] text-white h-10">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1B1E28] border-[#6E2BFF]">
                <SelectItem value="1" className="text-white">Niveau 1 (tous)</SelectItem>
                <SelectItem value="3" className="text-white">Niveau 3</SelectItem>
                <SelectItem value="5" className="text-white">Niveau 5</SelectItem>
                <SelectItem value="10" className="text-white">Niveau 10</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Type de récompense</label>
              <Select value={formData.reward_type} onValueChange={(value) => setFormData({ ...formData, reward_type: value })}>
                <SelectTrigger className="bg-[#1B1E28] border-[#6E2BFF] text-white h-10">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1B1E28] border-[#6E2BFF]">
                  <SelectItem value="ticket" className="text-white">🎟️ Billet</SelectItem>
                  <SelectItem value="physical" className="text-white">📦 Physique</SelectItem>
                  <SelectItem value="digital" className="text-white">💻 Digital</SelectItem>
                  <SelectItem value="experience" className="text-white">✨ Expérience</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Description récompense</label>
              <Input
                placeholder="Ex: Billet Paris Basket Ball"
                value={formData.reward_description}
                onChange={(e) => setFormData({ ...formData, reward_description: e.target.value })}
                className="bg-[#1B1E28] border-[#6E2BFF] text-white placeholder-[#B3B6C2]"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Quantité de récompense</label>
            <Input
              type="number"
              placeholder="Ex: 1"
              value={formData.reward_value}
              onChange={(e) => setFormData({ ...formData, reward_value: e.target.value })}
              className="bg-[#1B1E28] border-[#6E2BFF] text-white placeholder-[#B3B6C2]"
            />
          </div>



          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             <div>
               <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Début</label>
               <Input
                 type="datetime-local"
                 value={formData.start_at}
                 onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                 className="bg-[#1B1E28] border-[#6E2BFF] text-white"
               />
             </div>
             <div>
               <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Fin</label>
               <Input
                 type="datetime-local"
                 value={formData.end_at}
                 onChange={(e) => setFormData({ ...formData, end_at: e.target.value })}
                 className="bg-[#1B1E28] border-[#6E2BFF] text-white"
               />
             </div>
             </div>


           <div className="flex flex-col sm:flex-row gap-2">
             <Button
               onClick={handleSubmit}
               disabled={createChallengeMutation.isPending}
               className="w-full sm:flex-1 bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold disabled:opacity-50"
             >
               {createChallengeMutation.isPending ? 'Création en cours...' : 'Créer le défi'}
             </Button>
             <Button
               onClick={() => setShowForm(false)}
               variant="outline"
               className="w-full sm:flex-1 border-[#6E2BFF] text-[#6E2BFF] hover:bg-[#6E2BFF]/10"
             >
               Annuler
             </Button>
           </div>
         </Card>
       )}


       {/* Challenges List */}
       <div className="space-y-4">
         {challenges.length === 0 ? (
           <p className="text-[#B3B6C2] text-center py-8">Aucun challenge pour le moment</p>
         ) : (
           challenges.map(challenge => (
             <Card
               key={challenge.id}
               className="p-4 bg-[#13151D] border-l-4 border-l-[#6E2BFF] space-y-3"
             >
               <div>
                 <h3 className="text-lg font-black uppercase tracking-tight">{challenge.title}</h3>
                 <p className="text-sm text-[#B3B6C2] mt-1">{challenge.description}</p>
               </div>


               <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm pt-3 border-t border-[#1B1E28]">
                 <div>
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Type</p>
                   <p className="text-white font-bold mt-1">
                     {CONDITION_TYPES.find(t => t.value === challenge.condition_type)?.icon}
                   </p>
                 </div>
                 <div>
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Objectif</p>
                   <p className="text-white font-bold mt-1">{challenge.condition_value}</p>
                 </div>
                 <div>
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Reward</p>
                   <p className="text-white font-bold mt-1">{challenge.reward_value} tickets</p>
                 </div>
                 <div>
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Niv. min</p>
                   <p className="text-white font-bold mt-1">{challenge.min_level || 1}</p>
                 </div>
                 <div>
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Status</p>
                   <p className="text-white font-bold mt-1 text-xs">{challenge.status === 'active' && !challenge.is_active ? 'INACTIF' : challenge.status?.toUpperCase()}</p>
                 </div>
               </div>


               <div className="flex gap-2 pt-3 border-t border-[#1B1E28]">
                 <AdminActionMenu
                   primaryAction={{
                     label: 'Voir les détails',
                     icon: ChevronRight,
                     onClick: () => navigate(`/admin/challenges/${challenge.id}`)
                   }}
                   secondaryActions={[
                     challenge.status === 'draft' && {
                       label: 'Activer',
                       onClick: () => statusTransitionMutation.mutate({ challenge, newStatus: 'active' }),
                       disabled: mutatingChallengeId === challenge.id
                     },
                     challenge.status === 'active' && {
                       label: challenge.is_active ? 'Désactiver' : 'Activer',
                       onClick: () => toggleChallengeMutation.mutate({ challenge, newStatus: !challenge.is_active }),
                       disabled: mutatingChallengeId === challenge.id
                     },
                     challenge.status === 'active' && {
                       label: 'Fermer',
                       onClick: () => statusTransitionMutation.mutate({ challenge, newStatus: 'closed' }),
                       disabled: mutatingChallengeId === challenge.id
                     }
                   ].filter(Boolean)}
                   dangerousActions={[
                     {
                       label: 'Supprimer',
                       icon: Trash2,
                       message: `Supprimer le défi "${challenge.title}" ? Cette action est irréversible.`,
                       onConfirm: () => deleteChallengeMutation.mutate(challenge),
                       isPending: deleteChallengeMutation.isPending
                     }
                   ]}
                 />
               </div>
             </Card>
           ))
         )}
         </div>
         </>
         )}
         </div>
         </div>
         </>
         );
         }